import { Box, UnstyledButton } from '@mantine/core';
import { useLayoutEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import type { Timeframe } from '../../../data/events/usePaginatedEventsQuery.ts';

interface TimeframeToggleProps {
  value: Timeframe;
  onChange: (value: Timeframe) => void;
  labels: Record<Timeframe, string>;
  counts?: Partial<Record<Timeframe, number | undefined>>;
}

const OPTIONS: Timeframe[] = ['upcoming', 'all'];

/** Stable default, so the layout effect below does not run on every render. */
const NO_COUNTS: NonNullable<TimeframeToggleProps['counts']> = {};

/**
 * Pill toggle for the published tab, in the same shape as the dark pill buttons elsewhere in the
 * dashboard: a light track with the active option as a solid dark pill.
 *
 * The dark pill is a single absolutely positioned element that slides between the options rather
 * than a background on each button, so switching animates. Its position is measured from the
 * buttons instead of assumed, because the labels are translated and carry counts, so the two
 * halves are not the same width.
 */
export const TimeframeToggle: FC<TimeframeToggleProps> = ({ value, onChange, labels, counts = NO_COUNTS }) => {
  const buttonRefs = useRef<Partial<Record<Timeframe, HTMLButtonElement | null>>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const active = buttonRefs.current[value];
    if (!active) return;

    setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value, labels, counts]);

  return (
    <Box
      p={4}
      role="tablist"
      style={{
        // The parent Stack stretches its children, so the track needs to shrink to its content.
        alignSelf: 'flex-start',
        // The dashboard sits on a light grey panel, so the track has to be lighter than that
        // to read as a control rather than blend into the background.
        backgroundColor: '#FFFFFF',
        border: '1px solid var(--mantine-color-gigi-outlines-0)',
        borderRadius: 999,
        display: 'inline-flex',
        gap: 4,
        position: 'relative',
      }}
    >
      {indicator && (
        <Box
          aria-hidden
          style={{
            backgroundColor: '#191919',
            borderRadius: 999,
            bottom: 4,
            left: indicator.left,
            position: 'absolute',
            top: 4,
            transition: 'left 200ms ease, width 200ms ease',
            width: indicator.width,
          }}
        />
      )}

      {OPTIONS.map((option) => {
        const isActive = value === option;
        const count = counts[option];

        return (
          <UnstyledButton
            aria-selected={isActive}
            key={option}
            ref={(node) => {
              buttonRefs.current[option] = node;
            }}
            role="tab"
            style={{
              alignItems: 'center',
              borderRadius: 999,
              color: isActive ? '#FFFFFF' : '#808080',
              display: 'inline-flex',
              fontSize: 14,
              fontWeight: 500,
              gap: 8,
              lineHeight: 1,
              padding: '8px 18px',
              position: 'relative',
              transition: 'color 200ms ease',
              zIndex: 1,
            }}
            onClick={() => onChange(option)}
          >
            {labels[option]}

            <Box
              component="span"
              style={{
                backgroundColor: isActive ? 'rgba(255, 255, 255, 0.18)' : 'var(--mantine-color-gigi-light-grey-0)',
                borderRadius: 999,
                color: isActive ? '#FFFFFF' : '#808080',
                fontSize: 12,
                fontWeight: 600,
                minWidth: 22,
                padding: '3px 7px',
                textAlign: 'center',
                transition: 'background-color 200ms ease, color 200ms ease',
              }}
            >
              {count ?? '--'}
            </Box>
          </UnstyledButton>
        );
      })}
    </Box>
  );
};
